import React, { useEffect, useRef } from "react";
import { useWidgetPolls } from "@/hooks/polls/useWidgetPolls";
import { Link, useParams } from "react-router-dom";
import { useUserVotes } from "@/hooks/polls/useUserVotes";
import useCategories from "@/hooks/useCategories";
import { usePollFilters } from "@/hooks/polls/usePollFilters";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronUp, ChevronDown } from "lucide-react";
import CategoryFilters from "./CategoryFilters";
import ActivePollsSection from "./ActivePollsSection";
import PollsHeader from "./PollsHeader";
import PollsLoadingState from "./PollsLoadingState";
import PollsErrorState from "./PollsErrorState";
import PollsEmptyState from "./PollsEmptyState";
import ClosedPollsGrid from "./ClosedPollsGrid";
import { useCountry } from "@/contexts/country";
import { usePollVotesRealtime } from "@/hooks/polls/usePollVotesRealtime";
import { useUniversalPollVoting } from "@/hooks/polls/useUniversalPollVoting";
import { useCentralizedVoting } from "@/hooks/voting/useCentralizedVoting";

interface CountryPollsTabProps {
  countryName?: string;
}

const CountryPollsTab: React.FC<CountryPollsTabProps> = ({ countryName }) => {
  const { countryCode } = useParams<{ countryCode: string }>();
  const refreshTriggered = useRef(false);
  const lastCountryCode = useRef<string | undefined>(countryCode);

  const { userVotes, setUserVotes } = useUserVotes();
  const { categories } = useCategories();

  // Use centralized voting for permission checks
  const { validateVote } = useCentralizedVoting();

  const {
    polls: allPolls,
    setPolls,
    currentPollIndex,
    isLoading,
    fetchError,
    nextPoll,
    prevPoll,
    refresh,
    closedPolls,
    userVotes: pollUserVotes,
  } = useWidgetPolls(countryCode);

  // Set up realtime subscription for poll votes
  const { markRecentVote } = usePollVotesRealtime(
    allPolls,
    setPolls,
    userVotes,
    setUserVotes
  );

  // Use the universal voting hook
  const { handleVote, canVote: universalCanVote } = useUniversalPollVoting({
    polls: allPolls,
    setPolls,
    userVotes,
    setUserVotes,
    markRecentVote,
    onRefresh: refresh,
  });

  const {
    selectedCategory,
    setSelectedCategory,
    showClosedPolls,
    setShowClosedPolls,
    filteredPolls,
    filteredClosedPolls,
  } = usePollFilters(allPolls, closedPolls);

  useEffect(() => {
    if (!refreshTriggered.current || lastCountryCode.current !== countryCode) {
      lastCountryCode.current = countryCode;
      refreshTriggered.current = true;

      setTimeout(() => {
        refresh();
      }, 100);
    }
  }, [countryCode, refresh]);

  if (isLoading) {
    return <PollsLoadingState countryName={countryName} />;
  }

  if (fetchError) {
    return <PollsErrorState countryName={countryName} onRefresh={refresh} />;
  }

  if (!allPolls || allPolls.length === 0) {
    return <PollsEmptyState countryName={countryName} onRefresh={refresh} />;
  }

  const currentPoll = filteredPolls[currentPollIndex];
  const hasVoted = userVotes[currentPoll?.id] !== undefined;

  // Use centralized voting validation for permission checks
  const checkCanVote = async (pollId: string): Promise<boolean> => {
    try {
      return await validateVote(pollId);
    } catch (error) {
      console.error("Error validating vote permission:", error);
      return false;
    }
  };

  // Create a wrapper vote handler that uses the poll ID
  const handlePollVote = async (optionId: number) => {
    if (currentPoll) {
      await handleVote(currentPoll.id, optionId);
    }
  };

  // Generate appropriate cannot vote reason based on poll settings
  const getCannotVoteReason = (poll: any): string => {
    if (!poll) return "Poll not available";

    // If poll allows anonymous voting, public users can vote
    if (poll.anonymous_allowed) {
      return "";
    }

    // If poll allows public voting, authenticated users can vote
    if (poll.public_voting_allowed) {
      return "Please sign in to vote on this poll";
    }

    // Otherwise, only verified pollsters can vote
    return "This poll is only available to verified pollsters";
  };

  return (
    <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm border border-gray-200 w-full">
      {/* Back to polls link */}
      <div className="mb-4">
        <Link
          to="/polls"
          className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Back to All Polls
        </Link>
      </div>

      <PollsHeader
        countryName={countryName}
        currentIndex={currentPollIndex}
        totalPolls={filteredPolls.length}
        onRefresh={refresh}
      />

      <CategoryFilters
        categories={categories}
        selectedCategory={selectedCategory}
        setSelectedCategory={setSelectedCategory}
      />

      <div className="space-y-8">
        <section>
          <h3 className="text-lg font-semibold text-nation-blue mb-4">
            Active Polls
          </h3>
          {currentPoll && (
            <ActivePollsSection
              filteredPolls={filteredPolls}
              currentPoll={currentPoll}
              currentPollIndex={currentPollIndex}
              hasVoted={hasVoted}
              canVote={universalCanVote(currentPoll.id)}
              handleVote={handlePollVote}
              nextPoll={nextPoll}
              prevPoll={prevPoll}
              refresh={refresh}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              userVotes={userVotes}
              cannotVoteReason={getCannotVoteReason(currentPoll)}
            />
          )}
        </section>

        {filteredClosedPolls.length > 0 && (
          <section className="pt-6 border-t border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-nation-blue">
                Closed Polls
              </h3>
              <Button
                variant="ghost"
                onClick={() => setShowClosedPolls(!showClosedPolls)}
                className="text-sm"
              >
                {showClosedPolls ? (
                  <>
                    Hide <ChevronUp className="ml-1 h-4 w-4" />
                  </>
                ) : (
                  <>
                    Show <ChevronDown className="ml-1 h-4 w-4" />
                  </>
                )}
              </Button>
            </div>

            {showClosedPolls && (
              <div className="w-full overflow-x-auto">
                <ClosedPollsGrid
                  polls={filteredClosedPolls}
                  userVotes={userVotes}
                />
              </div>
            )}
          </section>
        )}

        {selectedCategory &&
          filteredPolls.length === 0 &&
          filteredClosedPolls.length === 0 && (
            <div className="py-6 text-center">
              <p className="text-gray-500 mb-4">
                No polls available in this category.
              </p>
              <Button
                onClick={() => setSelectedCategory(null)}
                variant="outline"
                size="sm"
              >
                View All Categories
              </Button>
            </div>
          )}
      </div>

      <div className="mt-6 pt-6 border-t border-gray-200">
        <p className="text-gray-600 text-sm">
          Results are updated in real-time.
        </p>
      </div>
    </div>
  );
};

export default CountryPollsTab;
